import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, first, map, Observable, of, shareReplay } from 'rxjs';
import { LoginCallback } from './models/login-callback';
import { LoginCallbackRequest } from './models/login-callback-request';
import { OauthAuthRequest } from './models/oauth-auth-request';
import { OauthTokenRequest } from './models/oauth-token-request';
import { OauthAuthResponse } from './models/oauth-auth-response';
import { AuthService } from './auth.service';
import { Organization } from './models/organization';
import { UserOrganization } from './models/user-organization';
import { OrganizationService } from './models/organization-service';
import { Client } from './models/client';
import { ConfigurationService } from './configuration.service';
import { CreateOrganizationRequest } from './models/create-organization-request';
import { LogoutResponse } from './models/logout-response';
import { PatchUserRequest, User } from './models/user';
import { IPendingUserInvitationResponse } from './models/pending-user-invitation.response';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  APP_API_URL = `${this.config.config?.APP_API_URL}`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private config: ConfigurationService
  ) {}

  getAuthToken(request: LoginCallbackRequest) {
    const org_param = request.org_id ? `?org_id=${request.org_id}` : '';
    return this.http
      .get<LoginCallback>(`${this.APP_API_URL}/login${org_param}`)
      .pipe(shareReplay());
  }

  oauthAuthorize(request: OauthAuthRequest) {
    const authHeaders = this.getAuthHeaders();
    let formData: FormData = new FormData();
    formData.append('client_id', request.client_id);
    formData.append('scope', '*');
    formData.append('response_type', request.response_type);
    formData.append('redirect_uri', request.redirect_uri);

    if (request.code_challenge != null) {
      formData.append('code_challenge', request.code_challenge);
    }
    if (request.code_challenge_method != null) {
      formData.append('code_challenge_method', request.code_challenge_method);
    }
    if (request.state != null) {
      formData.append('state', request.state);
    }

    return this.http
      .post<OauthAuthResponse>(
        `${this.APP_API_URL}/oauth/authorize`,
        formData,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  oauthToken(request: OauthTokenRequest) {
    let formData: FormData = new FormData();
    formData.append('grant_type', request.grant_type);
    formData.append('scope', request.scope);
    formData.append('code', request.code);
    formData.append('client_id', request.client_id);
    formData.append('client_secret', request.client_secret);

    return this.http
      .post(`${this.APP_API_URL}/oauth/token`, formData)
      .pipe(shareReplay());
  }

  logout(redirectUrl: string) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<LogoutResponse>(
        `${this.APP_API_URL}/logout?redirect_url=${redirectUrl}`,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  getOrganizationUsers(orgId: number) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<UserOrganization[]>(
        `${this.APP_API_URL}/organizations/${orgId}/users/`,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  createClient(orgId: number, request: Client) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .post<Client>(
        `${this.APP_API_URL}/organizations/${orgId}/clients/`,
        request,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  createOrganization(request: CreateOrganizationRequest) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .post<Organization>(
        `${this.APP_API_URL}/users/0/organizations/`,
        request,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  getOrganizationServices(orgId: number) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<OrganizationService[]>(
        `${this.APP_API_URL}/organizations/${orgId}/services/`,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  getOrganizationUser(orgId: number, userId: number) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<UserOrganization>(
        `${this.APP_API_URL}/organizations/${orgId}/users/${userId}`,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  getUserOrganizations(userId: number) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<Organization[]>(
        `${this.APP_API_URL}/users/${userId}/organizations/`,
        { headers: authHeaders }
      )
      .pipe(shareReplay());
  }

  getOrganization(orgId: number) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<Organization>(`${this.APP_API_URL}/organizations/${orgId}`, {
        headers: authHeaders,
      })
      .pipe(shareReplay());
  }

  getClient(clientId: string) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<Client>(`${this.APP_API_URL}/clients/${clientId}`, {
        headers: authHeaders,
      })
      .pipe(shareReplay());
  }

  getUser() {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<User>(`${this.APP_API_URL}/users/0`, { headers: authHeaders })
      .pipe(shareReplay());
  }

  patchUser(userDetails: PatchUserRequest) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .patch(`${this.APP_API_URL}/users/0`, userDetails, {
        headers: authHeaders,
      })
      .pipe(shareReplay());
  }

  getPendingInvitations(clientID: string | number) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<IPendingUserInvitationResponse[]>(
        `${this.APP_API_URL}/users/${clientID}/invitations/`,
        {
          headers: authHeaders,
        }
      )
      .pipe(shareReplay());
  }

  getAcceptInvitation(publicId: string) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<{ message: string }>(
        `${this.APP_API_URL}/invitations/${publicId}/accept`,
        {
          headers: authHeaders,
        }
      )
      .pipe(shareReplay());
  }

  getDeclineInvitation(publicId: string) {
    const authHeaders = this.getAuthHeaders();
    return this.http
      .get<{ message: string }>(
        `${this.APP_API_URL}/invitations/${publicId}/decline`,
        {
          headers: authHeaders,
        }
      )
      .pipe(shareReplay());
  }

  getAuthHeaders() {
    const token = this.authService.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token ?? ''}`,
    });
  }
}
